<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px]  tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-16 tw-rounded">
            <div>
                <div class="tw-px-2 tw-py-2 tw-flex tw-items-center tw-justify-between dark:tw-bg-gray-100 tw-bg-gray-50 tw-border-b tw-border-solid tw-font-semibold dark:tw-text-gray-700 tw-text-gray-500">
                    <div
                        class="tw-text-lg tw-flex tw-items-center tw-gap-2  dark:tw-bg-gray-100 tw-bg-gray-50">
                        <span class="tw-text-primary-500 tw-bg-white tw-py-0 tw-rounded tw-px-2 tw-border tw-border-solid tw-border-gray-200  tw-ms-auto">{{ props.item.id }}</span>
                        <span>{{ $t("common.order") }}</span>
                    </div>

                    <div class="tw-flex tw-gap-0.5 tw-min-w-[100px]  tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-p-0.5">
                        <button @click="selected = tabs[0]" class="tw-duration-200 tw-transition-all tw-ease-in-out tw-p-1 tw-px-2"
                            :class="[selected.key == 'summary' ? 'tw-p-1 tw-px-2 tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-xs tw-rounded tw-border-solid' : 'tw-text-xs tw-px-2']">
                            {{ $t("common.order_summary") }}
                        </button>
                        <button @click="selected = tabs[1]" class="tw-duration-200 tw-transition-all tw-ease-in-out tw-p-1 tw-px-2"
                            :class="[selected.key == 'detailed' ? 'tw-p-1 tw-px-2 tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-xs tw-rounded tw-border-solid' : 'tw-text-xs tw-px-2']"
                        >
                            {{ $t("common.details") }}
                        </button>
                    </div>
                </div>

                <!-- Creator Information Section -->
                <div v-if="creatorInfo || googleSheetInfo" class="tw-bg-white tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-flex-shrink-0">
                            <icon :icon="getCreatorIcon" class="tw-w-5 tw-h-5 tw-text-gray-600" />
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1">
                            <span class="tw-text-sm tw-text-gray-500">{{ $t("common.created_by") }}:</span>
                            <span class="tw-text-sm tw-font-medium tw-ms-1 tw-flex tw-items-center tw-gap-1">
                                <template v-if="creatorInfo">
                                    <span class="tw-text-primary-600">{{ creatorInfo.name }}</span>
                                </template>
                                <template v-else-if="googleSheetInfo">
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <span class="tw-text-green-600">{{ googleSheetInfo.name }}</span>
                                        <span v-if="googleSheetInfo.sheet_name" class="tw-text-xs tw-text-gray-500">({{ googleSheetInfo.sheet_name }})</span>
                                    </div>
                                </template>
                                <template v-else>
                                    {{ $t("common.unknown") }}
                                </template>
                            </span>
                        </div>
                    </div>
                </div>

                <div class="tw-bg-white">
                    <div class="tw-w-full tw-overflow-hidden">
                        <!-- No History State -->
                        <div v-if="!loading && (!data || data.length === 0)" class="tw-py-10 tw-px-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                            <div class="tw-bg-gray-50 tw-rounded-full tw-p-4 tw-mb-4">
                                <icon icon="solar:clock-circle-bold" class="tw-w-10 tw-h-10 tw-text-gray-400" />
                            </div>
                            <h3 class="tw-text-gray-700 tw-font-medium tw-text-lg tw-mb-2">{{ $t("common.no_history") }}</h3>
                            <p class="tw-text-gray-500 tw-text-sm tw-max-w-md">{{ $t("common.no_history_description") }}</p>
                        </div>

                        <!-- Content -->
                        <transition v-else class="tw-duration-200" enter-from-class="tw-opacity-0 tw-translate-x-[10px]"
                            leave-to-class="tw-opacity-0 -tw-translate-x-[10px]" mode="out-in">
                            <component :is="selected.component" :loading="loading" :data="data" />
                        </transition>
                    </div>
                    <DetailedHistory v-if="false" :loading="loading" :data="data" />
                </div>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { defineProps, defineEmits, ref, shallowRef, computed } from 'vue';
import DetailedHistory from './history/DetailedHistory.vue';
import SummaryHistory from './history/SummaryHistory.vue';
import { history } from "@/api/orders";

const emit = defineEmits(['update:visible']);
const loading = ref(false);
const data = ref([]);
const creatorInfo = ref(null);
const googleSheetInfo = ref(null);
const source = ref(null);

const props = defineProps({
    item: {
        required: true
    },
    visible: {
        required: true,
        default: false
    }
});

const tabs = [
  {
    key: "summary",
    component: SummaryHistory
  },
  {
    key: "detailed",
    component: DetailedHistory
  }
];

const selected = shallowRef(tabs[1]);

const onCancel = () => {
    emit('update:visible', false)
}

const getCreatorIcon = computed(() => {
    if (source.value === 'google_sheet') {
        return 'mdi:google-spreadsheet';
    } else if (source.value === 'user') {
        return 'solar:user-bold';
    }
    return 'solar:info-circle-bold';
});

const getData = async () => {
    loading.value = true;
    await history(props.item.id)
        .then((res) => {
            if (res.data.code == 'SUCCESS') {
                data.value = res.data.data;
                data.value.reverse();
                
                // Store creator information if available
                if (res.data.creator) {
                    creatorInfo.value = res.data.creator;
                }
                
                // Store Google Sheet information if available
                if (res.data.google_sheet) {
                    googleSheetInfo.value = res.data.google_sheet;
                }
                
                // Store source information
                source.value = res.data.source || 'unknown';
            }
        })
    loading.value = false;
}

getData();


</script>

<style>
.creator-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}
.creator-badge.user {
    background-color: rgba(var(--primary-500-rgb), 0.1);
    color: var(--primary-600);
}
.creator-badge.google {
    background-color: rgba(52, 168, 83, 0.1);
    color: #34a853;
}
</style>